/** Route table (SPEC §15). Screens are code-split per route. */
import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, Outlet, ScrollRestoration, type RouteObject } from 'react-router-dom';
import { useNavDirection } from './lib/nav';
import { useStay } from './lib/queries';
import { useSession } from './lib/session';
import { Loading, RequireStay } from './routes/layout';

const Entry = lazy(() => import('./routes/Entry'));
const FindMyStay = lazy(() => import('./routes/FindMyStay'));
const Inactive = lazy(() => import('./routes/Inactive'));
const Welcome = lazy(() => import('./routes/Welcome'));
const Home = lazy(() => import('./routes/Home'));
const Agreement = lazy(() => import('./routes/Agreement'));
const IdSnap = lazy(() => import('./routes/IdSnap'));
const Preferences = lazy(() => import('./routes/Preferences'));
const House = lazy(() => import('./routes/House'));
const Local = lazy(() => import('./routes/Local'));
const Feedback = lazy(() => import('./routes/Feedback'));
const StayPage = lazy(() => import('./routes/StayPage'));
const SharePhotos = lazy(() => import('./routes/SharePhotos'));

function L({ children }: { children: ReactNode }) {
  return <Suspense fallback={<Loading />}>{children}</Suspense>;
}

function Guarded({ children }: { children: ReactNode }) {
  return (
    <RequireStay>
      <L>{children}</L>
    </RequireStay>
  );
}

/** Tabs that also work with no stay, from the public house info (ADR-041, ADR-045). */
function HouseOk({ children }: { children: ReactNode }) {
  return (
    <RequireStay house={<L>{children}</L>}>
      <L>{children}</L>
    </RequireStay>
  );
}

/** "/" (My Stay) is Welcome until registration is complete, then Home (SPEC §15). */
function Root() {
  const stay = useStay();
  if (!stay.data) return <Loading />;
  return stay.data.registration.status === 'completed' ? <Home /> : <Welcome stay={stay.data} />;
}

/** /help (V1's address for it): the Help sheet, over My Stay (ADR-050). */
function HelpLink() {
  const { token } = useSession();
  return <Navigate to={token ? '/?help=1' : '/find-my-stay?help=1'} replace />;
}

/** Every screen: a new screen starts at the top and Back returns to where the guest was; pages
 *  slide the right way (lib/nav, ADR-050). */
function Shell() {
  useNavDirection();
  return (
    <>
      <Outlet />
      <ScrollRestoration />
    </>
  );
}

const screens: RouteObject[] = [
  { path: '/s/:code', element: <L><Entry /></L> },
  { path: '/b/:bookingId', element: <L><Entry /></L> },
  { path: '/find-my-stay', element: <L><FindMyStay /></L> },
  { path: '/inactive', element: <L><Inactive /></L> },
  {
    path: '/',
    element: <Guarded><Root /></Guarded>,
  },
  { path: '/register/agreement', element: <Guarded><Agreement /></Guarded> },
  { path: '/register/id', element: <Guarded><IdSnap /></Guarded> },
  { path: '/register/preferences', element: <Guarded><Preferences /></Guarded> },
  // The old "You're in!" screen; registration now lands on My Stay (Rook, 2026-10-19).
  { path: '/register/done', element: <Navigate to="/" replace /> },
  { path: '/house', element: <HouseOk><House /></HouseOk> },
  // The old guide detail and Where's the…? screens are House Guide sections now (ADR-049).
  { path: '/house/:sectionId', element: <Navigate to="/house" replace /> },
  { path: '/find', element: <Navigate to="/house?s=where" replace /> },
  { path: '/local', element: <HouseOk><Local /></HouseOk> },
  { path: '/feedback', element: <HouseOk><Feedback /></HouseOk> },
  // My Stay's own pages, e.g. Getting Here › Directions (ADR-050). They work signed out too.
  { path: '/my-stay/:sectionId/:groupId', element: <HouseOk><StayPage /></HouseOk> },
  // The photo contest with its upload (ADR-057): anyone in the house may share, signed in or not.
  { path: '/share-photos', element: <HouseOk><SharePhotos /></HouseOk> },
  { path: '/help', element: <HelpLink /> },
];

if (import.meta.env.DEV) {
  // Only referenced in dev builds, so the gallery chunk never ships to guests.
  const Gallery = lazy(() => import('./dev/Gallery'));
  screens.push({ path: '/dev/components', element: <L><Gallery /></L> });
}

screens.push({ path: '*', element: <Navigate to="/" replace /> });

export const routes: RouteObject[] = [{ element: <Shell />, children: screens }];

export const router = createBrowserRouter(routes);
